import React, { ReactElement, useCallback, useEffect } from 'react';
import { Button, Form, InputNumber, Spin } from 'antd';
import { SliderMarks } from 'antd/es/slider';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import IDeal, {
    IDealErrors,
} from '@/Api/Deal/IDeal';
import {
    dealErrorSetAction,
    dealGetAction,
    dealValueSetAction,
} from '@/Lead/actions';
import {
    dealSelector,
    errorsSelector,
} from '@/Lead/selectors';
import { Dispatch } from 'redux';
import MoneySign from '@/UI/MoneySign';
import LeftDrawerLayout from './LeftDrawerLayout';
import FormItem from '@/UI/FormItem';
import Slider from '@/UI/Slider';

export default function LoanDetails(): ReactElement {
    const dispatch: Dispatch = useDispatch();
    const navigate: NavigateFunction = useNavigate();
    const deal: IDeal = useSelector(dealSelector);
    const errors: IDealErrors = useSelector(errorsSelector);

    useEffect(() => {
        if (!deal.uuid) {
            dispatch(dealGetAction());
        }
    }, [
        deal,
        dispatch,
    ]);

    const setError: (key: keyof IDealErrors, value: string) => void = useCallback((key: keyof IDealErrors, value: string): void => {
        dispatch(dealErrorSetAction(key, value));
    }, [
        dispatch,
    ]);

    const setValue: (key: keyof IDeal, value: number) => void = useCallback((key: keyof IDeal, value: number): void => {
        dispatch(dealValueSetAction(key, value));
    }, [
        dispatch,
    ]);

    const validateLoanAmount: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!deal.loanAmount || deal.loanAmount === 0) {
            error = 'Please enter your loan amount';
        } else if (deal.loanAmount < 25000) {
            error = 'The loan amount must be at least $25,000';
        }

        setError('loanAmount', error);

        return !error;
    }, [
        deal,
        setError,
    ]);

    const onClickContinue: () => void = useCallback((): void => {
        if (!validateLoanAmount()) {
            return;
        }

        navigate('/get-your-quote/loan-purpose');
    }, [
        navigate,
        validateLoanAmount,
    ]);

    const onChangeLoanAmount: (value: number) => void = useCallback((value: number): void => {
        if (value < 0 || value > 99999999) {
            return;
        }

        setValue('loanAmount', value);
    }, [
        setValue,
    ]);

    const onChangeTermMonths: (value: number) => void = useCallback((value: number): void => {
        setValue('termMonths', value);
    }, [
        setValue,
    ]);

    const validateTermMonths: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!deal.termMonths) {
            error = 'Please select a loan term of at least 1 month';
        } else if (deal.termMonths > 24) {
            error = 'Please select a loan term of at most 24 months';
        }

        setError('termMonths', error);

        return !error;
    }, [
        deal,
        setError,
    ]);

    if (!deal.uuid) {
        return (
            <LeftDrawerLayout currentStep={2} pageName='loan-details'>
                <h3 className='headline'>What do you need?</h3>
                <Spin indicator={<LoadingOutlined spin={true} />} />
            </LeftDrawerLayout>
        );
    }

    const termMonthsMarks: SliderMarks = {
        1: 1,
        6: 6,
        12: 12,
        18: 18,
        24: 24,
    };

    return (
        <LeftDrawerLayout currentStep={2} pageName='loan-details'>
            <h3 className='headline'>What loan do you need?</h3>
            <p className='intro'>
                Give us an idea of what you are looking to achieve and we&apos;ll try and get as close as possible.
            </p>
            <Form>
                <FormItem
                    className='loan-amount'
                    extra='Between $25,000 - $10,000,000'
                    help={errors.loanAmount}
                    label='How much would you like to borrow?'
                    layout='vertical'
                    name='loanAmount'
                    validateStatus={errors.loanAmount ? 'error' : ''}
                >
                    <InputNumber
                        addonBefore={<MoneySign />}
                        className='dollar-value'
                        controls={false}
                        min={0}
                        placeholder='Enter required amount of money'
                        type='number'
                        value={deal.loanAmount}
                        onBlur={validateLoanAmount}
                        onChange={onChangeLoanAmount}
                    />
                </FormItem>
                <FormItem
                    className='term-months'
                    help={errors.termMonths}
                    label='How long do you need the loan for?'
                    layout='vertical'
                    name='termMonths'
                    validateStatus={errors.termMonths ? 'error' : ''}
                >
                    <InputNumber
                        controls={false}
                        min={0}
                        placeholder='From 1 to 24 months'
                        suffix={deal.termMonths !== 1 ? 'Months' : 'Month'}
                        type='number'
                        value={deal.termMonths}
                        onBlur={validateTermMonths}
                        onChange={onChangeTermMonths}
                    />
                    <Slider
                        className='term-months-slider'
                        marks={termMonthsMarks}
                        max={24}
                        min={1}
                        tooltip={{ open: false }}
                        value={deal.termMonths ?? 1}
                        onBlur={validateTermMonths}
                        onChange={onChangeTermMonths}
                    />
                </FormItem>
            </Form>
            <div className='actions'>
                <Link to='/get-your-quote'><Button className='previous'>Back</Button></Link>
                <Button type='primary' onClick={onClickContinue}>Save and continue</Button>
            </div>
        </LeftDrawerLayout>
    );
}

